import { HIGH_PERCENTAGE_BREAKPOINT } from '@/constants/chart.constants';
import {
  Insight,
  InsightLanguageDistribution,
  TimeRange,
} from '@/services/dashboard-service/dashboard.service.types';
import dayjs, { Dayjs } from 'dayjs';
import i18n, { t } from 'i18next';
import utc from 'dayjs/plugin/utc';
import { BusinessHourItem } from '@/services/business-hours/business-hours.types';
import { DownloadedFile } from '@/services/request/request.types';

dayjs.extend(utc);

export const calculatePercentage = (value: number | null, size: number) => {
  if (!value || size <= 0) return 0;

  const percent = (value / size) * 100;
  return percent < 1 ? Number(percent.toFixed(1)) : Math.floor(percent);
};

export const calculatePercentageDecimal = (value: number | null, size: number) =>
  value && size > 0 ? Number(((value / size) * 100).toFixed(2)) : 0;

export const sumDataValue = (data: Insight) => {
  const mapNullsToZero = Object.values(data).map((value) => (value ? value : 0));
  return mapNullsToZero.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

export const sumLanguageDistributionData = (data: InsightLanguageDistribution) => {
  const counts = Object.values(data).map((item) =>
    typeof item === 'number' ? item : item?.count || 0
  );
  return counts.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
};

export const isValueHigherThanBreakpoint = (value: number) => value >= HIGH_PERCENTAGE_BREAKPOINT;

export const calculateSecondsToMinutes = (duration: number) => {
  const time = new Date(duration * 1000);
  const hours = time.getUTCHours();
  const minutes = hours ? hours * 60 + time.getUTCMinutes() : time.getUTCMinutes();

  return `${String(minutes).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`;
};

export const getSortedInsight = (
  data: Insight | undefined,
  count?: number
): Insight | undefined => {
  if (!data) {
    return data;
  }
  const dataArray = Object.entries(data).map(([key, value]) => ({ name: key, value }));
  const sorted = dataArray.sort(
    ({ value: valueA }, { value: valueB }) => (valueB || 0) - (valueA || 0)
  );

  const sliced = sorted.slice(0, count);

  return sliced.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {});
};

export const calculateDateFromISOtoYYYY_MM_DD = (date: string) => {
  return new Date(date).toISOString().substring(0, 10);
};

/** Seconds as a number, or null when the value is absent or not numeric. */
export const durationSeconds = (value: number | string | null) => {
  if (value === null || value === '') return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) ? seconds : null;
};

/**
 * Seconds of an actual recording, or null when there is nothing to play. A recording's
 * duration arrives as a string, so raw truthiness would accept "0" and offer a play
 * button for a zero-length recording.
 */
export const playableSeconds = (value: number | string | null | undefined) => {
  const seconds = durationSeconds(value ?? null);
  return seconds !== null && seconds > 0 ? seconds : null;
};

export const hasPlayableDuration = (value: number | string | null | undefined) =>
  playableSeconds(value) !== null;

/**
 * The mockup's conversation stamp: "dziś, 12:45", "wczoraj, 17:23", else "23.09, 22:12".
 * Local calendar day and a 24-hour clock, so a 01:30 call in UTC+2 is not dated yesterday.
 */
export const formatConversationDateTime = (date: string) => {
  const at = new Date(date);
  const time = at.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  const midnight = (day: Date) => new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const days = Math.round((midnight(new Date()).getTime() - midnight(at).getTime()) / 86400000);

  if (days === 0) return t('dates.todayAt', { time });
  if (days === 1) return t('dates.yesterdayAt', { time });

  const day = String(at.getDate()).padStart(2, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  // The year only when it is not this one: the picker reaches back to 2024 and this column
  // is also the sort key, so two Septembers must not read alike.
  const year = at.getFullYear() === new Date().getFullYear() ? '' : `.${at.getFullYear()}`;
  return `${day}.${month}${year}, ${time}`;
};

export const calculateHourFromISODate = (date: string) => {
  return new Date(date).toLocaleString(i18n.language, { hour: 'numeric', minute: 'numeric' });
};

export const downloadCSVFile = (data: string) => {
  const csvBlob = new Blob([data], { type: 'text/csv' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `calls-export_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.csv`
  );
  const href = URL.createObjectURL(csvBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

export const downloadTrendingTopicsCSVFile = (data: string) => {
  const csvBlob = new Blob([data], { type: 'text/csv' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `trending-topics_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.csv`
  );
  const href = URL.createObjectURL(csvBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

/** Saves a file fetched with `getFileRequest` under the name the server gave it. */
export const downloadFile = ({ blob, fileName }: DownloadedFile) => {
  const aElement = document.createElement('a');
  aElement.setAttribute('download', fileName);
  const href = URL.createObjectURL(blob);
  aElement.href = href;
  aElement.click();
  URL.revokeObjectURL(href);
};

export const downloadAudioFile = (audioData: Blob, fileName: string = 'audio-file') => {
  const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
  const aElement = document.createElement('a');
  aElement.setAttribute(
    'download',
    `${fileName}_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.mp3`
  );
  const href = URL.createObjectURL(audioBlob);
  aElement.href = href;
  aElement.setAttribute('target', '_blank');
  aElement.click();
  URL.revokeObjectURL(href);
};

export const anonymizeEmail = (email: string) => {
  const emailArray = email.split('@');
  const halfOfTheFirstWord = Math.floor(emailArray[0].length / 2);

  emailArray[0] = emailArray[0]
    .split('')
    .map((char, index) => {
      if (index >= halfOfTheFirstWord) {
        return char;
      }
      return '*';
    })
    .join('');

  return emailArray.join('@');
};

export const anonymizePhone = (phone: string) => {
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length <= 4) return '*'.repeat(cleanPhone.length);

  const start = cleanPhone.slice(0, 3);
  const end = cleanPhone.slice(-2);
  const hidden = '*'.repeat(cleanPhone.length - 5);

  return `${start}${hidden}${end}`;
};

export function convertToSeconds(duration: string): number {
  const [minutes, seconds] = duration.split(':').map(Number);
  const totalSeconds = minutes * 60 + seconds;
  return totalSeconds;
}

export const countSeconds = (setTime: (value: number) => void, startTime: number) => {
  let totalSeconds = startTime;

  const intervalId = setInterval(() => {
    totalSeconds--;
    setTime(totalSeconds);

    if (totalSeconds <= 0) {
      clearInterval(intervalId);
    }
  }, 1000);

  return () => clearInterval(intervalId);
};

export const getRangeDate = (selectedFilter: TimeRange, startTime?: string, endTime?: string) => {
  if (startTime && endTime) {
    return {
      currentDate: calculateDateFromISOtoYYYY_MM_DD(endTime),
      newDate: calculateDateFromISOtoYYYY_MM_DD(startTime),
    };
  }

  const days =
    selectedFilter === '24h' ? 1 : selectedFilter === '1y' ? 365 : Number(selectedFilter.replace(/\D/g, ''));
  const currentDate = new Date();
  const newDate = new Date(currentDate);
  newDate.setDate(currentDate.getDate() - days);

  return {
    currentDate: calculateDateFromISOtoYYYY_MM_DD(currentDate.toISOString()),
    newDate: calculateDateFromISOtoYYYY_MM_DD(newDate.toISOString()),
  };
};

export const formatToHTML = (text: string) => {
  return text
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n-/g, '<br>-')
    .replace(/\n/g, '<br>')
    .replace(/^- (.*?)(?=\n|$)/gm, '<li>$1</li>')
    .replace(/<br><li>/g, '<ul><li>')
    .replace(/<\/li>(?!<li>)/g, '</li></ul>');
};

export function capitalizeFirstLetter(str: string) {
  return str.replace(/^./, (char) => char.toUpperCase());
}

export const calculateBarchartWidth = (numberOfItems = 9) => {
  if (numberOfItems > 9) return numberOfItems * 34;
  return '100%';
};

/**
 * Chart bucket label. Day buckets arrive as `YYYY-MM-DD` and hour buckets as ISO
 * with a time part, so the key's shape is the granularity.
 */
export const formatBucketLabel = (date: string): string =>
  date.includes('T') ? dayjs(date).format('HH:mm') : dayjs(date).format('D MMM');

// The API's `wday` is an English key whatever language the UI speaks. `date.format('dddd')`
// follows the dayjs locale, so under `pl` it yields 'poniedziałek' and matches no row.
const API_WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const getHoursForDate = (
  temporary: BusinessHourItem[],
  permanent: BusinessHourItem[],
  date: Dayjs | null
): BusinessHourItem[] => {
  if (!date) return [];

  const dateStr = date.format('YYYY-MM-DD');

  const tempHours = temporary.filter((item) => {
    if (!item.start_time) return false;
    // Parse ISO string with timezone info (not as UTC)
    const itemDate = dayjs(item.start_time).format('YYYY-MM-DD');
    return itemDate === dateStr;
  });

  if (tempHours.length > 0) return tempHours;

  const weekday = API_WEEKDAYS[date.day()];
  return permanent.filter((item) => item.wday === weekday);
};

export const formatTimeRange = (
  startTime: string | null,
  endTime: string | null
): string | null => {
  if (!startTime || !endTime) return null;
  // Extract time portion from ISO string (e.g., "2025-08-27T08:00:00.000-06:00" -> "08:00")
  const extractTime = (isoString: string) => {
    const match = isoString.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
  };
  return `${extractTime(startTime)} – ${extractTime(endTime)}`;
};
